import React, { useState, useMemo, memo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { FILTER_LABELS } from './SimpleFilters';
import { formatOpportunity } from '../utils/formatters';
import PageContainer from './PageContainer';

/**
 * The metric chips shown when an entry is expanded. Shared by the phone card and
 * the desktop table row so the two cannot describe the same entity differently.
 */
const MetricChips = ({ lead }) => (
  <div className="flex flex-wrap gap-2">
    {lead.forecasted_sales != null && (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-900/40 text-green-300">
        &#128176; &yen;{Number(lead.forecasted_sales).toLocaleString()} potential
      </span>
    )}
    {lead.industry && (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-900/40 text-purple-300">
        &#127970; {lead.industry}
      </span>
    )}
    {lead.type && (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-900/40 text-orange-300">
        &#128205; {lead.type}
      </span>
    )}
    {lead.last_contacted_at && (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-700 text-gray-200">
        &#128197; Last contact: {new Date(lead.last_contacted_at).toLocaleDateString()}
      </span>
    )}
  </div>
);

/** Sort options offered on the phone, where column headers do not exist. */
export const SORT_OPTIONS = [
  { field: '', label: 'Default order' },
  { field: 'name', label: 'Name' },
  { field: 'opportunity_score', label: 'Opportunity' },
  { field: 'status', label: 'Status' },
];

/**
 * "39 entries — customers and leads", plus how many visible records are not
 * in the list because they have no location (the API's X-Entities-Unplaced).
 * Without it a list missing records looked complete.
 */
export function entriesCaption(count, unplaced = 0) {
  const base = `${count} ${count === 1 ? 'entry' : 'entries'} — customers and leads`;
  return unplaced > 0 ? `${base} · ${unplaced} without a location (not on the map)` : base;
}

const ListView = ({ leads, activeFilter, unplaced = 0 }) => {
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedLeads, setExpandedLeads] = useState(new Set());

  const toggleExpanded = (leadId) => {
    setExpandedLeads(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
      } else {
        newSet.add(leadId);
      }
      return newSet;
    });
  };

  // Memoized sort: must be declared before the early return so hooks are always
  // called in the same order. Only recalculates when leads, sortField, or sortDirection change.
  const sortedLeads = useMemo(() => {
    if (!leads || leads.length === 0) return [];
    return [...leads].sort((a, b) => {
      if (!sortField) return 0;

      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle opportunity_score or numeric fields
      if (sortField === 'opportunity_score' || typeof aVal === 'number') {
        aVal = aVal || 0;
        bVal = bVal || 0;
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle string fields
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [leads, sortField, sortDirection]);

  if (!leads || leads.length === 0) {
    return (
      <div className="h-full w-full overflow-y-auto bg-gray-900 p-4 md:p-8 pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gray-800 rounded-xl shadow-lg p-12 text-center">
            <p className="text-gray-400 text-lg">
              {activeFilter === 'ai_picks'
                ? 'No AI recommendations yet. Generate a briefing to get personalized picks.'
                : 'Nothing to display for the current filter.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status, category) => {
    // Overrides for urgency
    if (status === 'at_risk') return 'bg-orange-900/40 text-orange-300';
    if (status === 'churned') return 'bg-red-900/40 text-red-300';

    // Category themes
    switch (category) {
      case 'current_customers': return 'bg-emerald-900/40 text-emerald-300';
      case 'won': return 'bg-green-900/40 text-green-300';
      case 'leads': return 'bg-blue-900/40 text-blue-300';
      case 'planned_visits': return 'bg-purple-900/40 text-purple-300';
      case 'inactive': return 'bg-gray-700 text-gray-300';
      default: return 'bg-blue-900/40 text-blue-300';
    }
  };

  const getPriorityEmoji = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return '🔥🔥🔥';
      case 'medium': return '⚡⚡';
      case 'low': return '❄️';
      default: return '—';
    }
  };


  // Full view with proper mobile scrolling
  return (
    <PageContainer wide>
        {/* Title - responsive */}
        {/* Titled by the filter that produced the list. It read "All Leads"
            regardless, which was wrong twice: the list holds customers as well
            as leads, and the heading ignored the active filter entirely. */}
        <h2 className="text-2xl md:text-3xl font-bold mb-1 text-gray-100">
          {FILTER_LABELS[activeFilter] || 'All Entities'}
        </h2>
        <p className="text-sm text-gray-400 mb-4 md:mb-6">
          {entriesCaption(leads.length, unplaced)}
        </p>

        {/* ── Phone: one card per entry ──
            The table needs 640px and a phone offers 390, so it used to sit
            behind a horizontal scroller with three of its five columns off the
            edge and no affordance that they existed — the same pattern removed
            from the mobile menu and the permission matrix. Cards carry the same
            fields down the screen instead, and match the visit and briefing
            cards a rep already knows. */}
        <div className="md:hidden space-y-3">
          <div className="flex items-center gap-2">
            <label htmlFor="list-sort" className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              Sort by
            </label>
            <select
              id="list-sort"
              value={sortField}
              onChange={(e) => { setSortField(e.target.value); setSortDirection('asc'); }}
              className="flex-1 px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {SORT_OPTIONS.map(o => <option key={o.field} value={o.field}>{o.label}</option>)}
            </select>
            {sortField && (
              <button
                onClick={() => setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'))}
                aria-label={sortDirection === 'asc' ? 'Sort descending' : 'Sort ascending'}
                className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-lg bg-gray-800 border border-gray-600 text-gray-300"
              >
                {sortDirection === 'asc' ? '↑' : '↓'}
              </button>
            )}
          </div>

          {sortedLeads.map((lead) => {
            const isExpanded = expandedLeads.has(lead.global_id);
            const score = lead.opportunity_score;
            return (
              <div
                key={lead.global_id}
                data-testid="list-card"
                onClick={() => toggleExpanded(lead.global_id)}
                className={`rounded-xl border p-4 transition-colors ${isExpanded ? 'bg-gray-800 border-blue-700' : 'bg-gray-800 border-gray-700'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-100 break-words">{lead.name || `Entity #${lead.id}`}</h3>
                    <p className="text-sm text-gray-400 mt-0.5 break-words">{lead.address || 'No address'}</p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="text-xl font-bold leading-none tabular-nums text-gray-100">
                      {formatOpportunity(score) ?? '—'}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">Opportunity</div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className={`whitespace-nowrap px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(lead.status, lead.category)}`}>
                    {(lead.status || lead.category || 'potential').toUpperCase().replace('_', ' ')}
                  </span>
                  <span className="text-sm" title={`${lead.priority || 'no'} priority`}>
                    {getPriorityEmoji(lead.priority)}
                  </span>
                  <span className="ml-auto text-xs font-semibold text-indigo-300">
                    {isExpanded ? 'Less' : 'Details'}
                  </span>
                </div>

                {isExpanded && (
                  <div className="mt-3 pt-3 border-t border-gray-700 space-y-3">
                    <div>
                      <span className="inline-block bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded text-xs font-semibold mb-2">
                        AI Summary
                      </span>
                      <p className="text-sm text-gray-300 leading-relaxed">
                        {lead.summary || 'No summary available for this entry.'}
                      </p>
                    </div>
                    <MetricChips lead={lead} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Desktop: the table, which the width genuinely supports ── */}
        <div className="hidden md:block bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-gray-900/50 border-b border-gray-700">
                <tr>
                  <th
                    className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-300 cursor-pointer hover:bg-gray-700 transition-colors"
                    onClick={() => handleSort('name')}
                  >
                    Lead {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-300 cursor-pointer hover:bg-gray-700 transition-colors"
                    onClick={() => handleSort('opportunity_score')}
                    title="How much business this place could be worth. Fixed to the place itself — unlike the briefing's Smart Score, it does not change with where you are."
                  >
                    Opportunity {sortField === 'opportunity_score' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-300 cursor-pointer hover:bg-gray-700 transition-colors"
                    onClick={() => handleSort('status')}
                  >
                    Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-300">
                    Priority
                  </th>
                  <th className="hidden md:table-cell px-6 py-4 text-left text-sm font-semibold text-gray-300">
                    Summary
                  </th>
                  <th className="px-2 md:px-4 py-3 md:py-4 text-center text-xs md:text-sm font-semibold text-gray-300">

                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {sortedLeads.map((lead) => {
                  const isExpanded = expandedLeads.has(lead.global_id);
                  return (
                    <React.Fragment key={lead.global_id}>
                      <tr
                        className={`hover:bg-gray-900/50 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-900/20' : ''}`}
                        onClick={() => toggleExpanded(lead.global_id)}
                      >
                        <td className="px-4 md:px-6 py-3 md:py-4">
                          <div className="font-semibold text-sm md:text-base text-gray-100">
                            {lead.name || `Entity #${lead.id}`}
                          </div>
                          <div className="text-xs md:text-sm text-gray-400 truncate max-w-[150px] md:max-w-none">
                            {lead.address || 'No address'}
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-3 md:py-4">
                          <div className="flex items-center gap-2">
                            <div className="text-lg md:text-xl font-bold text-gray-100">
                              {formatOpportunity(lead.opportunity_score) ?? '—'}
                            </div>
                            <div className="hidden md:block flex-1 bg-gray-700 rounded-full h-2 w-16 md:w-24">
                              <div
                                className={`h-2 rounded-full ${(lead.opportunity_score || 0) >= 80 ? 'bg-emerald-500' :
                                  (lead.opportunity_score || 0) >= 50 ? 'bg-blue-500' :
                                    'bg-gray-600'
                                  }`}
                                style={{ width: `${Math.min(100, lead.opportunity_score || 0)}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 md:px-6 py-3 md:py-4">
                          <span className={`px-2 md:px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(lead.status, lead.category)}`}>
                            {(lead.status || lead.category || 'potential').toUpperCase().replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 md:px-6 py-3 md:py-4">
                          <div className="flex items-center gap-1 text-base md:text-lg">
                            {getPriorityEmoji(lead.priority)}
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-6 py-4">
                          <div className="text-sm text-gray-400 max-w-md line-clamp-2">
                            {lead.summary || 'N/A'}
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-3 md:py-4">
                          <button
                            aria-label={isExpanded ? 'Hide details' : 'Show details'}
                            aria-expanded={isExpanded}
                            className="p-1 rounded-lg hover:bg-gray-700 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(lead.global_id);
                            }}
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        </td>
                      </tr>
                      {/* Expandable Summary Row */}
                      {isExpanded && (
                        <tr className="bg-gradient-to-r from-blue-900/30 to-indigo-900/10">
                          <td colSpan={6} className="px-4 md:px-6 py-4">
                            <div className="space-y-3">
                              {/* Full Summary */}
                              <div>
                                <div className="font-semibold text-gray-300 text-sm mb-2 flex items-center gap-2">
                                  <span className="bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded text-xs">AI Summary</span>
                                </div>
                                <p className="text-sm text-gray-300 leading-relaxed">
                                  {lead.summary || 'No summary available for this lead.'}
                                </p>
                              </div>

                              {/* Key Metrics */}
                              <div className="pt-2 border-t border-gray-700">
                                <MetricChips lead={lead} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
    </PageContainer>
  );
};

// Shallow prop memo: skips re-render when leads reference and activeFilter haven't changed.
// sortField/sortDirection are internal state so they don't factor into this check.
export default memo(ListView);